import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../../Common/Loading';
import submit_n from '../../Common/Images/submit_n.gif';
import submit_o from '../../Common/Images/submit_o.gif';
import submit_d from '../../Common/Images/submit_d.gif';
import close_n from '../../Common/Images/close_n.gif';
import close_o from '../../Common/Images/close_o.gif';
import close_d from '../../Common/Images/close_d.gif';

const btnImages: Record<string, string> = {
    'submit_n': submit_n, 'submit_o': submit_o, 'submit_d': submit_d || submit_o,
    'close_n': close_n, 'close_o': close_o, 'close_d': close_d
};

const ImageButton: React.FC<{ name: string; onClick?: () => void; style?: React.CSSProperties }> = ({ name, onClick, style }) => {
    const [suffix, setSuffix] = useState('n');
    const imageKey = `${name}_${suffix}`;
    const imageSrc = btnImages[imageKey];

    return (
        <img
            src={imageSrc} alt={name}
            style={{ cursor: 'pointer', verticalAlign: 'middle', ...style, paddingRight: '20px' }}
            onMouseEnter={() => setSuffix('o')}
            onMouseLeave={() => setSuffix('n')}
            onMouseDown={() => setSuffix('d')}
            onMouseUp={() => setSuffix('o')}
            onClick={onClick} draggable={false}
        />
    );
};


interface FamilyRecord {
    school_code: string;
    family_name: string;
    family_login: string;
    registered_on: string;
    reg_children: number;
    total_usage: string;
    email: string;
}

const EL_FamilyUsageAnalysis: React.FC = () => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
    const [searchParams] = useSearchParams();
    const querySchoolCode = searchParams.get('C');

    const getFormattedDate = (date: Date) => date.toISOString().split('T')[0];
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    const [entity, setEntity] = useState('DLR');
    const [schools, setSchools] = useState<string[]>([]);
    const [schoolCode, setSchoolCode] = useState('-- ALL --');
    const [surname, setSurname] = useState('');
    const [periodType, setPeriodType] = useState<'A' | 'P'>('A');

    const [fromDate, setFromDate] = useState(getFormattedDate(lastMonth));
    const [toDate, setToDate] = useState(getFormattedDate(today));

    const [data, setData] = useState<FamilyRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    document.body.style.backgroundColor = '#fff';

    useEffect(() => {
        document.body.style.fontFamily = 'Arial';
        document.body.style.fontSize = '12pt';
        document.body.style.marginTop = '5px';
        document.body.style.marginBottom = '5px';
    }, []);

    useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
        document.body.style.overflow = '';
    };
}, []);

    useEffect(() => {
        const fetchSchools = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}api/get_family_analysis_schools?entity=${entity}`);
                const json = await res.json();
                if (json.schools) {
                    setSchools(json.schools);

                    if (querySchoolCode && json.schools.includes(querySchoolCode)) {
                        setSchoolCode(querySchoolCode);
                    } else {
                        setSchoolCode('-- ALL --');
                    }
                }
            } catch (err) { console.error("School Fetch Error:", err); }
        };
        fetchSchools();
    }, [entity, querySchoolCode]);

    // --- Handlers ---
    const handleGo = async () => {
        setLoading(true);
        setHasSearched(true);

        try {
            const res = await fetch(`${API_BASE_URL}api/family_usage_analysis_report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    school_code: schoolCode,
                    surname: surname,
                    period_type: periodType,
                    from_date: fromDate,
                    to_date: toDate
                })
            });
            const json = await res.json();
            if (json.error) alert(json.error);
            else setData(json.results || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        window.location.href = "/SysAdmin";
    };

    // --- Styles mapping to legacy & eLearnCSS.css ---
    const styles = {
        mainContainer: {
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden' as const,
            margin: '0 auto',
        },

        // Filter Table matching the legacy inline styles
        filterTable: { width: '100%', border: 'solid 1px blue', borderCollapse: 'collapse' as const, marginBottom: '15px' },
        headerRow: { fontSize: '14pt', fontWeight: 'bold', color: 'Blue', backgroundColor: 'azure', borderBottom: 'solid 1px blue' },
        trPadding: { paddingBottom: '1px', paddingTop: '2px' },
        tdLabel: { fontSize: '12pt', padding: '3px' },

        input: { border: '1px solid #80a0ff', padding: '2px', width: '150px' },
        select: { border: '1px solid #80a0ff', padding: '2px', width: '150px' },

        // Results Grid mapping to eLearnCSS.css rules
        gridWrapper: {
            width: 'calc(100% - 2px)',
            maxWidth: 'calc(100% - 2px)',
            height: '75vh',
            overflowX: 'auto' as const,
            overflowY: 'auto' as const,
            border: 'solid 1px #80a0ff',
            marginTop: '10px',
            boxSizing: 'border-box' as const,
        },
        gridTable: {
            width: 'max-content',
            minWidth: '100%',
            borderCollapse: 'collapse' as const,
            fontFamily: 'Arial',
            fontSize: '8pt',
            color: 'black',
        },
        th: {
            backgroundColor: '#CFE2FF',
            fontSize: '14px',
            color: '#2859ac',
            fontWeight: 'normal',
            padding: '10px',
            textAlign: 'center' as const,
            border: '1px solid #80a0ff',

            position: 'sticky' as const,
            top: 0,
            zIndex: 10,

            whiteSpace: 'nowrap' as const,
        },

        td: {
            padding: '8px',
            fontSize: '14px',
            borderRight: '1px solid #80a0ff',
            textAlign: 'center' as const,
            whiteSpace: 'nowrap' as const,
        },
    };

    return (
        <div style={styles.mainContainer}>
            {loading && <Loading />}

            {/* Filter Area */}
            <table style={styles.filterTable}>
                <tbody>
                    <tr>
                        <td colSpan={7} style={{ ...styles.headerRow, padding: '5px', textAlign: 'center' }}>
                            Family Usage Analysis
                        </td>
                    </tr>
                    <tr style={styles.trPadding}>
                        <td style={{ ...styles.tdLabel, width: '15%' }}>Entity:</td>
                        <td style={{ ...styles.tdLabel, width: '20%' }}>
                            <select style={styles.select} value={entity} onChange={(e) => setEntity(e.target.value)}>
                                <option value="DLR">eDealer</option>
                                <option value="ISP">ISP</option>
                            </select>
                        </td>
                        <td style={{ ...styles.tdLabel, width: '15%' }}>School Code:</td>
                        <td style={{ ...styles.tdLabel, width: '20%' }}>
                            <select
                                style={styles.select}
                                value={schoolCode}
                                onChange={(e) => setSchoolCode(e.target.value)}
                                disabled={!!querySchoolCode} // Disabled if passed via URL
                            >
                                <option value="-- ALL --">-- ALL --</option>
                                {schools.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </td>
                        <td style={{ ...styles.tdLabel, width: '10%', textAlign: 'right' }}>Surname:</td>
                        <td style={{ ...styles.tdLabel, width: '15%' }}>
                            <input type="text" style={styles.input} value={surname} onChange={(e) => setSurname(e.target.value)} />
                        </td>
                        <td style={{ ...styles.tdLabel, width: '5%', textAlign: 'right' }}>
                            <ImageButton name="submit" onClick={handleGo} style={{ width: '80px', height: '30px' }} />
                        </td>
                    </tr>
                    <tr style={{ paddingBottom: '5px', paddingTop: '1px' }}>
                        <td style={styles.tdLabel}>Registered on:</td>
                        <td colSpan={2} style={styles.tdLabel}>
                            <label style={{ marginRight: '15px', cursor: 'pointer' }}>
                                <input type="radio" name="rdoPD" value="A" checked={periodType === 'A'} onChange={() => setPeriodType('A')} style={{ verticalAlign: 'middle' }} /> ALL dates
                            </label>
                            <label style={{ cursor: 'pointer' }}>
                                <input type="radio" name="rdoPD" value="P" checked={periodType === 'P'} onChange={() => setPeriodType('P')} style={{ verticalAlign: 'middle' }} /> Specify period
                            </label>
                        </td>
                        <td colSpan={3} style={{ ...styles.tdLabel, visibility: periodType === 'P' ? 'visible' : 'hidden' }}>
                            <input type="date" style={styles.input} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                            &nbsp;To&nbsp;
                            <input type="date" style={styles.input} value={toDate} onChange={(e) => setToDate(e.target.value)} />
                        </td>
                        <td style={{ ...styles.tdLabel, textAlign: 'right' }}>
                            <ImageButton name="close" onClick={handleClose} style={{ width: '80px', height: '30px' }} />
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Error / Empty state handling */}
            {!loading && hasSearched && data.length === 0 && (
                <div style={{ color: 'red', fontWeight: 'bold', marginTop: '10px', textAlign: 'center' }}>
                    No Records Found
                </div>
            )}

            {/* Results Grid */}
            {data.length > 0 && (
                <div style={styles.gridWrapper}>
                    <table style={styles.gridTable}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, textAlign: 'left' }}>#</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>SchoolCode</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>Family Name</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>FamilyLogin</th>
                                <th style={styles.th}>RegisteredOn</th>
                                <th style={styles.th}>Reg.Children</th>
                                <th style={styles.th}>TotalUsage</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>EMail</th>
                            </tr>
                        </thead>

                        <tbody>
                            {data.map((row, idx) => (
                                <tr
                                    key={`${row.school_code}-${row.family_login}`}
                                    style={{
                                        backgroundColor:
                                            idx % 2 === 0 ? 'white' : '#f5faff'
                                    }}
                                >
                                    <td style={{ ...styles.td, textAlign: 'left' }}>
                                        {idx + 1}
                                    </td>

                                    <td style={{ ...styles.td, textAlign: 'left' }}>
                                        {row.school_code}
                                    </td>

                                    <td style={{ ...styles.td, textAlign: 'left' }}>
                                        {row.family_name}
                                    </td>

                                    <td style={{ ...styles.td, textAlign: 'left' }}>
                                        {row.family_login}
                                    </td>

                                    <td style={styles.td}>
                                        {row.registered_on}
                                    </td>

                                    <td style={styles.td}>
                                        {row.reg_children}
                                    </td>

                                    <td style={styles.td}>
                                        {row.total_usage}
                                    </td>

                                    <td style={{ ...styles.td, textAlign: 'left' }}>
                                        {row.email}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default EL_FamilyUsageAnalysis;